// El dashboard del Supervisor aquí ve los proyectos nuevos para asignar, monitorea el progreso de los que ya están en proceso y revisa proyectos para subir
import React, { useState, useEffect } from 'react';
import { ProjectManagementModal } from '../components/UI/ProjectManagementModal';
import { ConfirmationModal } from '../components/UI/ConfirmationModal';
import { ActionWithReasonModal } from '../components/UI/ActionWithReasonModal';
import { VacationRequestsTable } from '../components/shared/VacationRequestsTable';
import { ProjectsTable } from '../components/shared/ProjectsTable';
import { PROYECTOS_COLLECTION} from '../utils/constants'
import { onSnapshot } from 'firebase/firestore';

import { 
    collection, query, where, getDocs, doc, updateDoc, Timestamp
} from 'firebase/firestore';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { db } from '../firebase/config'
import { useUser } from '../context/UserContext';

export const SupervisorDashboard = ({ selectedRole }) => {
    const { userData, user } = useUser();
    const [view, setView] = useState('new');
    const [allProjects, setAllProjects] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [reviewProjects, setReviewProjects] = useState([]);
    const [vacationRequests, setVacationRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalProject, setModalProject] = useState(null);
    const [assignModalProject, setAssignModalProject] = useState(null);

    const fetchData = async () => {
        try {
            const qProjects = query(collection(db, PROYECTOS_COLLECTION), where("estado", "!=", "Cotización"));
            const qTechsOld = query(collection(db, "usuarios"), where("rol", "==", "tecnico"));
            const qTechsNew = query(collection(db, "usuarios"), where("roles", "array-contains", "tecnico"));
            const qReview = query(collection(db, PROYECTOS_COLLECTION), where("estado", "==", "En Revisión Final"));
            const qVacations = query(collection(db, "solicitudesVacaciones"), where("estado", "==", "Pendiente Supervisor"));

            const [projectsSnap, techsOldSnap, techsNewSnap, reviewSnap, vacationsSnap] = await Promise.all([
                getDocs(qProjects),
                getDocs(qTechsOld),
                getDocs(qTechsNew),
                getDocs(qReview),
                getDocs(qVacations)
            ]);

            const projectsData = projectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllProjects(projectsData);
            const techMap = new Map();
            techsOldSnap.forEach(doc => techMap.set(doc.id, { id: doc.id, ...doc.data() }));
            techsNewSnap.forEach(doc => techMap.set(doc.id, { id: doc.id, ...doc.data() }));
            setTechnicians(Array.from(techMap.values()));
            setReviewProjects(reviewSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setVacationRequests(vacationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        } catch (err) {
            console.error("Error fetching supervisor data:", err);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        setLoading(true);

        const qProjects = query(collection(db, PROYECTOS_COLLECTION), where("estado", "!=", "Cotización"));
        const qTechs = query(collection(db, "usuarios"));
        const qReview = query(collection(db, PROYECTOS_COLLECTION), where("estado", "==", "En Revisión Final"));
        const qVacations = query(collection(db, "solicitudesVacaciones"), where("estado", "==", "Pendiente Supervisor"));

        const handleError = (err) => {
            if (err.code !== 'permission-denied') {
                console.error("Error en suscripción:", err);
            }
        };

        const unsubProjects = onSnapshot(qProjects, (snap) => {
            setAllProjects(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, handleError);

        const unsubTechs = onSnapshot(qTechs, (snap) => {
            const techs = snap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(u => u.rol === 'tecnico' || (u.roles && u.roles.includes('tecnico')));
            setTechnicians(techs);
        }, handleError);

        const unsubReview = onSnapshot(qReview, (snap) => {
            setReviewProjects(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, handleError);

        const unsubVacations = onSnapshot(qVacations, (snap) => {
            setVacationRequests(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, handleError);

        return () => {
            unsubProjects();
            unsubTechs();
            unsubReview();
            unsubVacations();
        };
    }, []);

    const processedData = React.useMemo(() => {
        if (technicians.length === 0) return { healthData: [], newProjects: [], projectsByTechnician: {}, projectsForReview: [] };

        const projectsByTechnician = {};
        technicians.forEach(tech => {
            projectsByTechnician[tech.id] = allProjects.filter(p =>
                p.estado === 'Activo' && p.asignadoTecnicosIds?.includes(tech.id)
            );
        });

        const healthData = technicians.map(tech => {
            const assignedProjects = allProjects.filter(p => p.asignadoTecnicosIds?.includes(tech.id));
            const activeProjects = assignedProjects.filter(p => p.estado === 'Activo' && !p.fechaFinTecnicoReal);
            const fase2PendingCount = assignedProjects.filter(p => p.estado === 'Activo' && p.faseFacturacion === 'Fase 2 Pendiente').length;

            const horasAsignadas = activeProjects.reduce((sum, p) => {
                const remainingHours = Math.max(0, (p.horasEstimadas || 0) - (p.horasRegistradas || 0));
                return sum + remainingHours;
            }, 0);

            const capacidadSemanal = (tech.horasDisponiblesDiarias || 8) * 5;
            const saturacion = capacidadSemanal > 0 ? (horasAsignadas / capacidadSemanal) * 100 : 0;
            const diasParaTerminar = (tech.horasDisponiblesDiarias || 8) > 0 ? horasAsignadas / (tech.horasDisponiblesDiarias || 8) : 0;
            const deliveredInYear = allProjects.filter(p => 
                p.asignadoTecnicosIds?.includes(tech.id) &&
                (p.estado === 'Archivado' || p.estado === 'Facturado') &&
                (p.fase1_fechaFinTecnico || p.fechaFinTecnicoReal)?.toDate().getFullYear() === new Date().getFullYear()
            ).length;

            return {
                id: tech.id,
                nombre: tech.nombreCompleto,
                proyectosActivos: activeProjects.length,
                proyectosEntregados: deliveredInYear,
                horasAsignadas,
                capacidadSemanal,
                saturacion,
                diasParaTerminar,
                proyectosFase2Pendiente: fase2PendingCount,
            };
        });
        
        const newProjects = allProjects.filter(p => p.estado === 'Activo' && (!p.asignadoTecnicosIds || p.asignadoTecnicosIds.length === 0));
        
        for (const techId in projectsByTechnician) {
            projectsByTechnician[techId].sort((a, b) => (b.prioridad || "").localeCompare(a.prioridad || ""));
        }

        return { healthData, newProjects, projectsByTechnician };
    }, [allProjects, technicians]);

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Panel de Supervisión</h1>
            {loading ? <p>Calculando métricas del equipo...</p> : (
                <div className="grid grid-cols-1 lg:grid-cols-[1fr,3fr] gap-8">

                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold text-gray-700">Vistas de Gestión</h2>

                        <button onClick={() => setView('new')} className={`w-full p-3 rounded-lg text-left transition-colors ${view === 'new' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white hover:bg-gray-50'}`}>
                            <div className="flex justify-between items-center">
                                <span className="font-bold">Nuevos por Asignar</span>
                                {processedData.newProjects.length > 0 && <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">{processedData.newProjects.length}</span>}
                            </div>
                        </button>
                        <button onClick={() => setView('review')} className={`w-full p-3 rounded-lg text-left transition-colors ${view === 'review' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white hover:bg-gray-50'}`}>
                             <div className="flex justify-between items-center">
                                <span className="font-bold">Revisión Final</span>
                                {reviewProjects.length > 0 && <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">{reviewProjects.length}</span>}
                            </div>
                        </button>

                        <button onClick={() => setView('vacations')} className={`w-full p-3 rounded-lg text-left transition-colors ${view === 'vacations' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white hover:bg-gray-50'}`}>
                             <div className="flex justify-between items-center">
                                <span className="font-bold">Solicitudes Vacaciones</span>
                                {vacationRequests.length > 0 && <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">{vacationRequests.length}</span>}
                            </div>
                        </button>

                        <hr className="my-4"/>
                        <h2 className="text-lg font-semibold text-gray-700">Equipo Técnico</h2>
                        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
                            {processedData.healthData.map(tech => (
                                <button key={tech.id} onClick={() => setView(tech.id)} className={`w-full text-left transition-colors ${view === tech.id ? 'ring-2 ring-blue-500' : ''}`}>
                                    <TechnicianHealthCard techData={tech} />
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="min-w-0">
                        {view === 'new' && (
                            <ProjectsTable 
                                projects={processedData.newProjects} 
                                userRole="supervisor" 
                                supervisorView="new" 
                                onAssignClick={setAssignModalProject} 
                                user={user} userData={userData} selectedRole={selectedRole}
                            />
                        )}
                        
                        {view === 'review' && (
                            <ReviewProjectsTable projects={reviewProjects} onUpdateProject={fetchData} />
                        )}
                        
                        {view === 'vacations' && (
                            <VacationRequestsTable 
                                requests={vacationRequests} 
                                viewerRole="supervisor" 
                                onActionComplete={fetchData} 
                            />
                        )}

                        {processedData.projectsByTechnician[view] && (
                            <ProjectsTable
                                projects={processedData.projectsByTechnician[view]}
                                userRole="supervisor"
                                supervisorView="techDetail"
                                onManageClick={setModalProject}
                                onAssignClick={setAssignModalProject}
                                user={user} userData={userData} selectedRole={selectedRole}
                            />
                        )}
                    </div>
                </div>
            )}
            
            {modalProject && <ProjectManagementModal project={modalProject} onClose={() => setModalProject(null)} onUpdate={fetchData} user={user} userData={userData} userRole="supervisor" />}
            {assignModalProject && <AssignProjectModal project={assignModalProject} onClose={() => setAssignModalProject(null)} onFinalized={fetchData} />}
        </div>
    );
};

// Tabla de proyectos en revision 
const ReviewProjectsTable = ({ projects, onUpdateProject }) => {
    const [confirmingAction, setConfirmingAction] = useState(null);

    const handleApprove = async () => {
        if (!confirmingAction || confirmingAction.action !== 'approve') return;
        const { project } = confirmingAction.payload;
        const projectRef = doc(db, PROYECTOS_COLLECTION, project.id);
        const updatePayload = {};

        if (project.urlNotaPdf2) {
            updatePayload.estado = 'Terminado';
            updatePayload.estadoCliente = 'Terminado';
        } 
        else if (project.urlNotaPdf1) {
            updatePayload.estado = 'Activo'; 
            updatePayload.faseFacturacion = 'Fase 2 Pendiente'; 
            updatePayload.estadoCliente = 'Terminado';

            if (project.asignadoTecnicosIds) {
                const newTechStatus = {};
                project.asignadoTecnicosIds.forEach(uid => {
                    newTechStatus[uid] = 'No Visto';
                });
                updatePayload.tecnicosStatus = newTechStatus;
            }
            const alreadyBilled = project.facturasClienteIds && project.facturasClienteIds.length > 0;
            if (!alreadyBilled) {
                updatePayload.necesitaFactura = true;
            }
        }
        
        try {
            await updateDoc(projectRef, updatePayload);
            toast.success("Proyecto procesado correctamente.");
        } catch (err) {
            console.error(err);
            toast.error("Error al actualizar el proyecto.");
        }
        
        setConfirmingAction(null);
        onUpdateProject();
    };

    const handleReject = async (reason) => {
        if (!confirmingAction || confirmingAction.action !== 'reject') return;
        const { projectId } = confirmingAction.payload;

        if (reason && reason.trim() !== '') {
            const projectRef = doc(db, PROYECTOS_COLLECTION, projectId);
            await updateDoc(projectRef, { estado: 'Terminado Internamente', motivoRechazo: reason });
            onUpdateProject();
        } else {
            alert("El motivo del rechazo no puede estar vacío.");
            return;
        }
        setConfirmingAction(null);
    };

    const promptApprove = (project) => {
        const isPhase2Finish = !!project.fase2_urlNotaEntregaFirmada;
        const isPhase1Reactivation = !!project.fase1_urlNotaEntregaFirmada && !isPhase2Finish;

        let message = "¿Confirmar aprobación?";
        
        if (isPhase2Finish) {
            message = "El proyecto se marcará como TERMINADO. ¿Continuar?";
        } else if (isPhase1Reactivation) {
            message = "El proyecto pasara a pendiente de resolutivo ¿Continuar?";
        }

        setConfirmingAction({
            action: 'approve',
            payload: { project },
            title: "Confirmar Aprobación",
            message: message,
        });
    };

    const promptReject = (projectId) => {
        setConfirmingAction({
            action: 'reject',
            payload: { projectId },
            title: "Rechazar Proyecto",
            message: "Por favor, introduce el motivo del rechazo para notificar al practicante.",
            confirmText: "Rechazar",
            confirmColor: "bg-orange-600"
        });
    };

    return (
        <>
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">NPU</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documentos</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {projects.map(project => (
                             <tr key={project.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{project.clienteNombre}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{project.servicioNombre}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{project.npu}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <div className="flex flex-col space-y-1">
                                        {project.urlHeyzine && <a href={project.urlHeyzine} target="_blank" rel="noopener noreferrer" className="text-blue-600">Ver Proyecto</a>}
                                        {project.fase1_urlNotaEntregaFirmada && <a href={project.fase1_urlNotaEntregaFirmada} target="_blank" rel="noopener noreferrer" className="text-red-600">Nota F1</a>}
                                        {project.fase2_urlNotaEntregaFirmada && <a href={project.fase2_urlNotaEntregaFirmada} target="_blank" rel="noopener noreferrer" className="text-red-600">Nota F2</a>}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex flex-col space-y-1">
                                        <button onClick={() => promptApprove(project)} className="text-green-600 hover:text-green-900">Aprobar</button>
                                        <button onClick={() => promptReject(project.id)} className="text-orange-600 hover:text-orange-900">Rechazar</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {confirmingAction?.action === 'approve' && <ConfirmationModal title={confirmingAction.title} message={confirmingAction.message} onConfirm={handleApprove} onCancel={() => setConfirmingAction(null)} />}
            {confirmingAction?.action === 'reject' && <ActionWithReasonModal title={confirmingAction.title} message={confirmingAction.message} onConfirm={handleReject} onCancel={() => setConfirmingAction(null)} confirmText={confirmingAction.confirmText} confirmColor={confirmingAction.confirmColor} />}
        </>
    );
};


//para las tarjetas de salud de los tecnicos que se visualizan en el panel del supervisor
const TechnicianHealthCard = ({ techData }) => {
    const {
        nombre,
        proyectosActivos,
        proyectosEntregados,
        horasAsignadas,
        capacidadSemanal,
        saturacion,
        diasParaTerminar,
        proyectosFase2Pendiente,
    } = techData;

    const getSaturationColor = (percentage) => {
        if (percentage > 100) return 'bg-red-500';
        if (percentage > 85) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 flex flex-col space-y-4">
            <h3 className="text-xl font-bold text-gray-800 truncate">{nombre}</h3>
            
            <div>
                <div className="flex justify-between text-sm font-medium text-gray-600">
                    <span>Saturación (Horas Semanales)</span>
                    <span>{horasAsignadas.toFixed(1)}h / {capacidadSemanal}h</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 mt-1">
                    <div
                        className={`h-4 rounded-full ${getSaturationColor(saturacion)}`}
                        style={{ width: `${Math.min(saturacion, 100)}%` }}
                    ></div>
                </div>
                 {saturacion > 100 && <p className="text-xs text-red-600 font-semibold mt-1">¡Sobrecargado por {(saturacion - 100).toFixed(0)}%!</p>}
            </div>

            <div className="grid grid-cols-2 gap-4 text-center border-t border-b py-4">
                <div>
                    <p className="text-2xl font-bold">{proyectosActivos}</p>
                    <p className="text-sm text-gray-500">Activos</p>
                </div>
                <div>
                    <p className="text-2xl font-bold">{proyectosEntregados}</p>
                    <p className="text-sm text-gray-500">Entregados (2025)</p>
                </div>
                <div>
                    <p className="text-2xl font-bold">{diasParaTerminar.toFixed(1)}</p>
                    <p className="text-sm text-gray-500">Días para Terminar</p>
                </div>
                <div>
                    <p className="text-2xl font-bold text-gray-600">{proyectosFase2Pendiente}</p>
                    <p className="text-sm text-gray-500">Pend. resolutivo</p>
                </div>
            </div>

        </div>
    );
};

const AssignProjectModal = ({ project, onClose, onFinalized }) => {
    const [technicians, setTechnicians] = useState([]);
    const [selectedTechnicianId, setSelectedTechnicianId] = useState(
        (project.asignadoTecnicosIds && project.asignadoTecnicosIds[0]) || ''
    );
    const [deliveryDate, setDeliveryDate] = useState(project.fechaEntregaInterna ? project.fechaEntregaInterna.toDate().toISOString().split('T')[0] : '');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchTechnicians = async () => {
            const q1 = query(collection(db, "usuarios"), where("rol", "==", "tecnico"));
            const q2 = query(collection(db, "usuarios"), where("roles", "array-contains", "tecnico"));

            const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);

            const techMap = new Map();
            snapshot1.forEach((doc) => techMap.set(doc.id, { id: doc.id, ...doc.data() }));
            snapshot2.forEach((doc) => techMap.set(doc.id, { id: doc.id, ...doc.data() }));
            
            setTechnicians(Array.from(techMap.values()));
        };
        fetchTechnicians();
    }, []);

    const handleSave = async () => {
        if (!selectedTechnicianId) {
            alert("Debes seleccionar un técnico.");
            return;
        }
        setLoading(true);
        const projectRef = doc(db, PROYECTOS_COLLECTION, project.id);
        
        const tecnicosStatus = {};
        tecnicosStatus[selectedTechnicianId] = project.tecnicosStatus?.[selectedTechnicianId] || "No Visto";

        await updateDoc(projectRef, {
            asignadoTecnicosIds: [selectedTechnicianId],
            tecnicosStatus: tecnicosStatus,
            fechaAsignacionTecnico: Timestamp.now(),
            fechaEntregaInterna: deliveryDate ? Timestamp.fromDate(new Date(deliveryDate)) : null
        });
        setLoading(false);
        onFinalized();
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                <h3 className="text-lg font-bold mb-4">Asignar Proyecto: {project.npu}</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Técnico Asignado</label>
                        <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2">
                            {technicians.map(tech => (
                                <div key={tech.id} className="flex items-center">
                                    <input
                                        type="radio"
                                        name="technician"
                                        id={tech.id}
                                        value={tech.id}
                                        checked={selectedTechnicianId === tech.id}
                                        onChange={(e) => setSelectedTechnicianId(e.target.value)}
                                        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <label htmlFor={tech.id} className="ml-3 text-sm text-gray-700">{tech.nombreCompleto}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Fecha Límite de Entrega (Interna)</label>
                        <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md"/>
                    </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                    <button onClick={onClose} disabled={loading} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded">Cancelar</button>
                    <button onClick={handleSave} disabled={loading} className="bg-[#b0ef26] hover:bg-[#9ac91e] text-black font-bold py-2 px-4 rounded">{loading ? 'Asignando...' : 'Guardar Asignación'}</button>
                </div>
            </div>
        </div>
    );
};